import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  ToolbarItem,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useSalesInvoices } from '../../../hooks/useAdminReconciliation';
import type { SalesInvoiceBasis, SalesInvoiceRow, SalesInvoicesShow } from '../../../types/models/Reconciliation';
import { formatBareDateNumeric, formatInstantBrussels } from '../../../utils/dateFormat';
import { StatusMultiSelect } from '../Verda/VerdaGpuParts';
import {
  AMOUNT_CELL,
  CsvButton,
  EmptyListState,
  FetchedAt,
  FlagLabels,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  MonthPicker,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  SettlementRef,
  StaleDataAlert,
  distinctOptions,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import { DetailList } from './AttributionDetails';
import {
  ORIGIN_LABELS,
  PAYE_LABELS,
  PAYMENT_METHOD_LABELS,
  SALES_TYPE_LABELS,
  SETTLED_VIA_LABELS,
  labelOf,
} from './reconciliationLabels';
import { buildSalesCsv, reconciliationCsvFilename } from './reconciliationCsv';

/** The « Statut Facturier » filter value for a document without status. */
const NO_PAYE = '__none__';

/** Search: invoice number, client (Facturier and Manager names), title, Mollie payment id. */
export const matchSalesInvoice = (r: SalesInvoiceRow, q: string) =>
  matchesQuery(q, [r.numero, r.client_name, r.manager_client_names, r.intitule, r.mollie_payment_id]);

const SETTLED_VIA_COLORS: Record<string, 'blue' | 'purple' | 'orange' | 'teal' | 'grey' | 'red'> = {
  mollie: 'blue',
  mollie_prepaid: 'blue',
  wallet: 'purple',
  wallet_prepaid: 'purple',
  wallet_pending: 'orange',
  outside_manager: 'teal',
  none: 'red',
  credit_note: 'grey',
};

const SettledViaLabel: React.FunctionComponent<{ value: string }> = ({ value }) => (
  <Label color={SETTLED_VIA_COLORS[value] ?? 'grey'} isCompact>
    {labelOf(SETTLED_VIA_LABELS, value)}
  </Label>
);

/** The client as Facturier names it, else the linked Manager organization(s). */
const clientText = (r: SalesInvoiceRow) => r.client_name ?? (r.manager_client_names || '—');

/** Where the money went: the Mollie payout, the wallet charge, or nothing. */
const SettlementCell: React.FunctionComponent<{ row: SalesInvoiceRow }> = ({ row }) => {
  if (row.mollie_settlement_id !== null) {
    return <SettlementRef id={row.mollie_settlement_id} reference={row.mollie_settlement_reference} />;
  }
  if (row.mollie_payment_id !== null) {
    return <span className="pf-v6-u-text-color-subtle">Mollie, pas encore versé</span>;
  }
  if (row.wallet_charged_on !== null) {
    return <span>Solde, le {formatBareDateNumeric(row.wallet_charged_on)}</span>;
  }
  return <>—</>;
};

const TotalsCards: React.FunctionComponent<{ data: SalesInvoicesShow }> = ({ data }) => {
  const { totals } = data;
  return (
    <Grid hasGutter className="pf-v6-u-mb-md">
      <GridItem md={6} xl={4}>
        <Card isFullHeight isCompact>
          <CardTitle>Totaux du mois</CardTitle>
          <CardBody>
            <DescriptionList isCompact isHorizontal>
              <DescriptionListGroup>
                <DescriptionListTerm>Documents</DescriptionListTerm>
                <DescriptionListDescription>{totals.count}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>HTVA</DescriptionListTerm>
                <DescriptionListDescription>
                  <Money value={totals.amount_htva} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>TVA</DescriptionListTerm>
                <DescriptionListDescription>
                  <Money value={totals.amount_tva} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>TVAC</DescriptionListTerm>
                <DescriptionListDescription>
                  <strong>
                    <Money value={totals.amount_tvac} />
                  </strong>
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
            <p className="pf-v6-u-mt-sm pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">Notes de crédit en négatif.</p>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem md={6} xl={4}>
        <Card isFullHeight isCompact>
          <CardTitle>Par mode de règlement</CardTitle>
          <CardBody>
            <Table aria-label="Totaux par mode de règlement" variant="compact">
              <Thead>
                <Tr>
                  <Th>Réglée via</Th>
                  <Th className={AMOUNT_CELL}>Nombre</Th>
                  <Th className={AMOUNT_CELL}>TVAC</Th>
                </Tr>
              </Thead>
              <Tbody>
                {totals.by_settled_via.map((b) => (
                  <Tr key={b.settled_via}>
                    <Td dataLabel="Réglée via">{labelOf(SETTLED_VIA_LABELS, b.settled_via)}</Td>
                    <Td dataLabel="Nombre" className={AMOUNT_CELL}>
                      {b.count}
                    </Td>
                    <Td dataLabel="TVAC" className={AMOUNT_CELL}>
                      <Money value={b.tvac} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem md={12} xl={4}>
        <Card isFullHeight isCompact>
          <CardTitle>Par statut Facturier (factures)</CardTitle>
          <CardBody>
            <Table aria-label="Totaux des factures par statut Facturier" variant="compact">
              <Thead>
                <Tr>
                  <Th>Statut</Th>
                  <Th className={AMOUNT_CELL}>Nombre</Th>
                  <Th className={AMOUNT_CELL}>TVAC</Th>
                </Tr>
              </Thead>
              <Tbody>
                {totals.by_paye.map((b) => (
                  <Tr key={b.paye ?? 'none'}>
                    <Td dataLabel="Statut">{b.paye === null ? 'Sans statut' : labelOf(PAYE_LABELS, b.paye)}</Td>
                    <Td dataLabel="Nombre" className={AMOUNT_CELL}>
                      {b.count}
                    </Td>
                    <Td dataLabel="TVAC" className={AMOUNT_CELL}>
                      <Money value={b.tvac} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </GridItem>
    </Grid>
  );
};

const COLUMN_COUNT = 11;

interface SalesJournalTabProps {
  month: string;
  onMonthChange: (month: string) => void;
  basis: SalesInvoiceBasis;
  onBasisChange: (basis: SalesInvoiceBasis) => void;
  isActive: boolean;
}

/**
 * Journal des ventes — `GET /admin/reconciliation/sales-invoices`: every Facturier invoice and credit note
 * of the month (all clients), issued in it or marked paid in it, with how each was settled.
 */
const SalesJournalTab: React.FunctionComponent<SalesJournalTabProps> = ({
  month,
  onMonthChange,
  basis,
  onBasisChange,
  isActive,
}) => {
  const { data, loading, error, fetchedAt, refetch } = useSalesInvoices(month, basis, isActive);
  const rows = useMemo(() => data?.invoices ?? [], [data]);
  const [settledVia, setSettledVia] = useState<string[]>([]);
  const [paye, setPaye] = useState<string[]>([]);
  const [origins, setOrigins] = useState<string[]>([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  const settledViaOptions = useMemo(
    () => distinctOptions([...rows.map((r) => r.settled_via), ...settledVia], (v) => labelOf(SETTLED_VIA_LABELS, v)),
    [rows, settledVia],
  );
  const payeOptions = useMemo(() => {
    const options = distinctOptions([...rows.map((r) => r.paye), ...paye.filter((p) => p !== NO_PAYE)], (v) =>
      labelOf(PAYE_LABELS, v),
    );
    return rows.some((r) => r.paye === null) || paye.includes(NO_PAYE)
      ? [...options, { value: NO_PAYE, label: 'Sans statut' }]
      : options;
  }, [rows, paye]);
  const originOptions = useMemo(
    () => distinctOptions([...rows.map((r) => r.origin), ...origins], (v) => labelOf(ORIGIN_LABELS, v)),
    [rows, origins],
  );

  const predicate = useCallback(
    (r: SalesInvoiceRow) =>
      (settledVia.length === 0 || settledVia.includes(r.settled_via)) &&
      (paye.length === 0 || paye.includes(r.paye ?? NO_PAYE)) &&
      (origins.length === 0 || origins.includes(r.origin)) &&
      (!flaggedOnly || r.flags.length > 0),
    [settledVia, paye, origins, flaggedOnly],
  );
  const filterKey =
    settledVia.length + paye.length + origins.length > 0 || flaggedOnly
      ? JSON.stringify([settledVia, paye, origins, flaggedOnly])
      : '';
  const list = useClientList(rows, matchSalesInvoice, predicate, filterKey);

  const clearFilters = () => {
    list.setSearchValue('');
    setSettledVia([]);
    setPaye([]);
    setOrigins([]);
    setFlaggedOnly(false);
  };
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <MonthPicker month={month} onChange={onMonthChange} />
      </FlexItem>
      <FlexItem>
        <ToggleGroup aria-label="Base du journal des ventes" isCompact>
          <ToggleGroupItem
            text="Émises dans le mois"
            buttonId="sales-basis-issued"
            isSelected={basis === 'issued'}
            onChange={(_e, selected) => selected && onBasisChange('issued')}
          />
          <ToggleGroupItem
            text="Payées dans le mois"
            buttonId="sales-basis-paid"
            isSelected={basis === 'paid'}
            onChange={(_e, selected) => selected && onBasisChange('paid')}
          />
        </ToggleGroup>
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
    </Flex>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState
            title="Impossible de charger le journal des ventes"
            message={error}
            onRetry={() => void refetch()}
          />
        ) : (
          <LiveReadSpinner what="journal des ventes" />
        )}
      </>
    );
  }

  const filters = (
    <>
      <ToolbarItem>
        <StatusMultiSelect
          options={settledViaOptions}
          selected={settledVia}
          onChange={setSettledVia}
          ariaLabel="Filtrer par mode de règlement"
          label="Réglée via"
        />
      </ToolbarItem>
      <ToolbarItem>
        <StatusMultiSelect
          options={payeOptions}
          selected={paye}
          onChange={setPaye}
          ariaLabel="Filtrer par statut Facturier"
          label="Statut Facturier"
        />
      </ToolbarItem>
      <ToolbarItem>
        <StatusMultiSelect
          options={originOptions}
          selected={origins}
          onChange={setOrigins}
          ariaLabel="Filtrer par origine"
          label="Origine"
        />
      </ToolbarItem>
      <ToolbarItem alignSelf="center">
        <Switch
          id="sales-flagged-only"
          label="Signalées seulement"
          isChecked={flaggedOnly}
          onChange={(_e, checked) => setFlaggedOnly(checked)}
        />
      </ToolbarItem>
    </>
  );

  return (
    <>
      {header}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners
        warnings={data.warnings}
        context={{ period: data.period }}
        onRetry={() => void refetch()}
      />
      {basis === 'paid' && (
        <p className="pf-v6-u-mb-md pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
          Par date de paiement Facturier. Jusqu&apos;au 16/10/2026, le Manager estampillait les factures payées via
          Mollie avec la date UTC : une telle facture payée entre 0 h et 2 h (heure de Bruxelles) le 1er du mois figure
          dans le mois précédent, et elle est signalée « Date de paiement décalée ».
        </p>
      )}
      <TotalsCards data={data} />
      {rows.length === 0 ? (
        <EmptyListState
          title={basis === 'paid' ? 'Aucun document payé ce mois-ci' : 'Aucun document émis ce mois-ci'}
        />
      ) : (
        <>
          <ListToolbar
            list={list}
            placeholder="Rechercher par n°, client, intitulé ou paiement Mollie..."
            filters={filters}
            actions={
              <CsvButton
                label="Exporter le mois (CSV)"
                filename={reconciliationCsvFilename.sales(month, basis)}
                build={() => buildSalesCsv(rows)}
              />
            }
          />
          {list.filtered.length === 0 ? (
            <NoMatchState onClear={clearFilters} />
          ) : (
            <>
              <Table aria-label="Journal des ventes du mois" variant="compact" isExpandable>
                <Thead>
                  <Tr>
                    <Th screenReaderText="Détails" />
                    <Th>N°</Th>
                    <Th>Date</Th>
                    <Th>Client</Th>
                    <Th>Intitulé</Th>
                    <Th>Statut Facturier</Th>
                    <Th className={AMOUNT_CELL}>TVAC</Th>
                    <Th className={AMOUNT_CELL}>Solde</Th>
                    <Th>Réglée via</Th>
                    <Th>Versement</Th>
                    <Th>Signalements</Th>
                  </Tr>
                </Thead>
                {list.paginated.map((r, rowIndex) => {
                  const isExpanded = expanded.has(r.facturier_invoice_id);
                  return (
                    <Tbody key={r.facturier_invoice_id} isExpanded={isExpanded}>
                      <Tr isContentExpanded={isExpanded}>
                        <Td
                          expand={{
                            rowIndex,
                            isExpanded,
                            onToggle: () => toggle(r.facturier_invoice_id),
                            expandId: 'sales-row',
                          }}
                        />
                        <Td dataLabel="N°" modifier="nowrap">
                          {r.numero ?? '—'}
                          {r.is_credit_note && (
                            <Label color="grey" isCompact className="pf-v6-u-ml-xs">
                              NC
                            </Label>
                          )}
                        </Td>
                        <Td dataLabel="Date" modifier="nowrap">
                          {formatBareDateNumeric(r.date)}
                        </Td>
                        <Td dataLabel="Client">{clientText(r)}</Td>
                        <Td dataLabel="Intitulé" modifier="breakWord">
                          {r.intitule ?? '—'}
                        </Td>
                        <Td dataLabel="Statut Facturier">
                          {r.paye === null ? '—' : labelOf(PAYE_LABELS, r.paye)}
                          {r.paid_on !== null && (
                            <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                              le {formatBareDateNumeric(r.paid_on)}
                            </div>
                          )}
                        </Td>
                        <Td dataLabel="TVAC" className={AMOUNT_CELL}>
                          <Money value={r.signed_tvac} />
                        </Td>
                        <Td dataLabel="Solde" className={AMOUNT_CELL}>
                          <Money value={r.solde} />
                        </Td>
                        <Td dataLabel="Réglée via">
                          <SettledViaLabel value={r.settled_via} />
                        </Td>
                        <Td dataLabel="Versement">
                          <SettlementCell row={r} />
                        </Td>
                        <Td dataLabel="Signalements">
                          <FlagLabels flags={r.flags} />
                        </Td>
                      </Tr>
                      <Tr isExpanded={isExpanded}>
                        <Td />
                        <Td colSpan={COLUMN_COUNT - 1}>
                          <ExpandableRowContent>
                            <DetailList
                              items={[
                                { term: 'Type', value: labelOf(SALES_TYPE_LABELS, r.type) },
                                { term: 'Origine', value: labelOf(ORIGIN_LABELS, r.origin) },
                                {
                                  term: 'HTVA · TVA · TVAC',
                                  value: (
                                    <>
                                      <Money value={r.amount_htva} /> · <Money value={r.amount_tva} /> ·{' '}
                                      <Money value={r.amount_tvac} />
                                    </>
                                  ),
                                },
                                {
                                  term: 'Organisations Manager',
                                  value:
                                    r.manager_clients.length > 0
                                      ? r.manager_client_names
                                      : 'Client Facturier uniquement',
                                },
                                { term: 'Clé comptable', value: r.accounting_key },
                                { term: 'Encaissements', value: r.collections },
                                {
                                  term: 'Montant encaissé',
                                  value: r.settlement_amount === null ? null : <Money value={r.settlement_amount} />,
                                },
                                { term: 'Paiement Mollie', value: r.mollie_payment_id },
                                {
                                  term: 'Moyen Mollie',
                                  value:
                                    r.mollie_method === null ? null : labelOf(PAYMENT_METHOD_LABELS, r.mollie_method),
                                },
                                {
                                  term: 'Payé chez Mollie le',
                                  value: r.mollie_paid_at === null ? null : formatInstantBrussels(r.mollie_paid_at),
                                },
                                {
                                  term: 'Versé le',
                                  value:
                                    r.mollie_settled_at === null ? null : formatInstantBrussels(r.mollie_settled_at),
                                },
                                {
                                  term: 'Débité du solde le',
                                  value:
                                    r.wallet_charged_on === null ? null : formatBareDateNumeric(r.wallet_charged_on),
                                },
                                {
                                  term: 'Mouvement de solde',
                                  value: r.wallet_transaction_id === null ? null : `#${r.wallet_transaction_id}`,
                                },
                                {
                                  term: 'Tentative de paiement',
                                  value: r.invoice_payment_id === null ? null : `#${r.invoice_payment_id}`,
                                },
                                {
                                  term: 'Commande de domaine',
                                  value: r.domain_order_id === null ? null : `#${r.domain_order_id}`,
                                },
                                {
                                  term: 'Facture GPU',
                                  value: r.gpu_server_invoice_id === null ? null : `#${r.gpu_server_invoice_id}`,
                                },
                                {
                                  term: 'Créditée par',
                                  value: r.credited_by_invoice_id === null ? null : `ID ${r.credited_by_invoice_id}`,
                                },
                                {
                                  term: 'Crédite la facture',
                                  value: r.crediting_invoice_id === null ? null : `ID ${r.crediting_invoice_id}`,
                                },
                                { term: 'ID Facturier', value: r.facturier_invoice_id },
                              ]}
                            />
                          </ExpandableRowContent>
                        </Td>
                      </Tr>
                    </Tbody>
                  );
                })}
              </Table>
              <ListPagination list={list} variant="bottom" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default SalesJournalTab;
